import Image from 'next/image';
import Link from 'next/link';
import { Trans, useTranslation } from 'next-i18next';
import type { ReactNode } from 'react';
import { useEffect, useRef } from 'react';

import { useMainVisualIllustrationNote, useMainVisualTranslation } from '@/components/approved-content/main-visuals/translation';
import visualStyles from '@/components/approved-content/main-visuals/visuals.module.css';
import { HOME_NOVA_BLOCK_CLASS, HOME_NOVA_INNER_CLASS, HOME_NOVA_SECTION_CLASS, useInView } from '@/components/home-nova/shell';
import { marcaMotion, useHomeMotionAtivo } from '@/components/motion/ativacao';
import { CamadasBloco, motionClasses } from '@/components/motion/camadas';
import { useMotionBloco, useMotionOff, useVisivel } from '@/components/motion/use-home-motion';
import { homeNovaNamespace } from '@/constants/feature-toggles/home-nova';
import { prefetchTrialSignUp, useTrialSignUp } from '@/contexts/trial-sign-up-context';
import { useBrandMarker } from '@/utils/home-intl/brand-glossary';
import { importWithRetry } from '@/utils/import-with-retry';
import { localeToDir } from '@/utils/seo';

import { homeIntlTrialHref, useHomeIntl } from '../shared/home-intl-context';
import { TrialCtaText, useIsAssineVersao } from '../shared/trial-cta-text';
import type { VideoVariant } from './hologram';
import { Hologram, HOLOGRAM_FIXED, hologramSummaryId } from './hologram';
import { VIDEO_POSTER_PLACEHOLDER } from './poster-placeholders';
import { RotaVideos, useRotaVideos } from './rota';
import styles from './videos.module.css';

export const HOME_NOVA_VIDEOS_ID = 'videos';
const NAMESPACE = homeNovaNamespace('videos');
const ASSETS = '/assets/images/home-nova/videos';

const VIDEOS: ReadonlyArray<{ variant: VideoVariant; key: 'without' | 'with'; file: string; cardClass: string }> = [
  {
    variant: 'sem',
    key: 'without',
    file: 'cs2-sem-noping',
    cardClass: 'border-white/[.08] shadow-[0_24px_60px_-30px_rgba(0,0,0,.8),0_0_0_1px_rgba(255,107,107,.05)]',
  },
  {
    variant: 'com',
    key: 'with',
    file: 'cs2-com-noping',
    cardClass: 'border-[rgba(147,255,24,.4)] shadow-[0_0_0_1px_rgba(147,255,24,.16),0_28px_70px_-28px_rgba(147,255,24,.38),0_24px_60px_-30px_rgba(0,0,0,.8)] hover:shadow-[0_0_0_1px_rgba(147,255,24,.22),0_32px_80px_-26px_rgba(147,255,24,.48),0_24px_60px_-30px_rgba(0,0,0,.85)]',
  },
];

const FEATURES = [
  { key: 'multi_internet', icon: 'globe.svg' },
  { key: 'multi_connection', icon: 'lightning.svg' },
  { key: 'ai', icon: 'sparkle.svg' },
] as const;

/** Classes do cartão de recurso de hoje; com o motion ligado o cartão ganha `relative overflow-hidden` (o traço da borda). */
const FEAT_CLASS = 'm-0 flex items-start gap-3 rounded-[14px] border border-white/[.07] bg-white/[.025] p-[18px] transition-[border-color,background-color,transform] duration-[250ms] ease-[ease] hover:-translate-y-0.5 hover:border-[rgba(147,255,24,.28)] hover:bg-[rgba(147,255,24,.04)] motion-reduce:transition-none';

/**
 * Bloco 2 da home nova (`#videos`, toggle `home_nova_videos`): o mesmo lance de CS2 com e sem NoPing, lado a lado.
 * Port de pt/home/blocks/b2-videos-v14 (+ b2-videos-v13). O texto, os cartões e o CTA vêm no SSR; os vídeos têm
 * preload="none" e, já no HTML, um pôster leve embutido (quadro do lance em 64 × 36, data URI de ~370 bytes, sem
 * requisição: ver poster-placeholders.ts), para nunca haver quadro vazio; o controlador (videos-controller.ts, carregado com import() quando a grade chega a 1,5 tela de distância)
 * troca pelo pôster do tamanho × DPR, sincroniza os dois vídeos, toca-os só na tela e anima os hologramas. Âncora `#videos` (o hero linka "Veja o NoPing em ação"); cabe inteiro numa tela nas 60 resoluções
 * (regras em videos.module.css).
 */
const CTA_ARROW = (
  <svg className={styles.ctaArrow} aria-hidden='true' viewBox='0 0 16 16' width='16' height='16'>
    <path d='M3 8h9.5M8.5 4 12.5 8l-4 4' fill='none' stroke='currentColor' strokeWidth='1.6' strokeLinecap='round' strokeLinejoin='round' />
  </svg>
);

/**
 * Sem o controlador (o pedaço de JS dele não baixou nem nas novas tentativas), os vídeos não tocam e ficariam no pôster
 * embutido de 64 × 36, borrado: troca pelo pôster original, que é o que o controlador mostraria numa tela grande.
 */
function showFullPosters(grid: HTMLElement) {
  grid.querySelectorAll<HTMLVideoElement>('video[data-poster-1280]').forEach((video) => {
    video.setAttribute('poster', video.getAttribute('data-poster-1280') as string);
  });
}

export function HomeNovaVideos({ visualOnly = false }: { visualOnly?: boolean }) {
  const { t: legacyT, i18n } = useTranslation(NAMESPACE);
  const t = useMainVisualTranslation(NAMESPACE, legacyT, visualOnly);
  const mark = useBrandMarker();
  const illustrationNote = useMainVisualIllustrationNote(NAMESPACE, visualOnly);
  // Owner decision 2026-10-07: no "illustrative" disclaimers anywhere, and the numeric readouts stay visible without them.
  const hideReadouts = false;
  const { openTrialSignUp } = useTrialSignUp();
  const [gridRef, isNear] = useInView<HTMLDivElement>({ rootMargin: '150% 0px' });
  const intlPage = useHomeIntl();
  const isAssine = useIsAssineVersao();
  const dir = intlPage ? intlPage.dir : localeToDir(i18n?.language ?? 'pt');
  const formatLoss = (value: number) => t('holo.loss_value', { value });
  const formatLossRef = useRef(formatLoss);
  formatLossRef.current = formatLoss;

  // Motion (motion.ts + rota.tsx + src/components/motion), só nos pares da lista: chegada dos vídeos, rota sem × com ligada
  // à rolagem, traço nos cartões de recurso e feixe no CTA. Fora da lista, o HTML e o comportamento de antes.
  const motionAtivo = useHomeMotionAtivo();
  const m = marcaMotion(motionAtivo);
  const sectionRef = useRef<HTMLElement>(null);
  const feixeRef = useRef<HTMLSpanElement>(null);
  useRotaVideos(sectionRef, motionAtivo && !visualOnly);
  // a chegada do kit dispara com o topo do bloco a 66% da tela: selo e título entram à vista
  useMotionBloco(sectionRef, { ativo: motionAtivo, cena: () => import('./motion').then((mod) => mod.cenaVideos), start: 'top 66%' });
  useVisivel([feixeRef], motionAtivo);
  // em Off (movimento reduzido...) o vídeo segue exatamente como na Home de antes
  const motionOff = useMotionOff(motionAtivo);
  const motionNoVideo = motionAtivo && !motionOff;

  useEffect(() => {
    const grid = gridRef.current;
    if (!isNear || !grid) return undefined;
    let isCancelled = false;
    let stop: (() => void) | undefined;
    // o pedaço do controlador pode não baixar (rede, bloqueador): nova tentativa e, sem ele, os pôsteres inteiros (SCRUM-2597)
    importWithRetry(() => import('./videos-controller'), { isCancelled: () => isCancelled })
      .then(({ startVideosController }) => {
        if (isCancelled) return;
        // motion: pula o flash branco antes do desfecho e marca a tela de morte do "Sem" (data-morto); fora da lista, nada muda
        stop = startVideosController(grid, { formatLoss: (value) => formatLossRef.current(value), motion: motionNoVideo });
      })
      .catch(() => {
        if (!isCancelled) showFullPosters(grid);
      });
    return () => {
      isCancelled = true;
      stop?.();
    };
  }, [isNear, gridRef, motionNoVideo]);

  // Home internacional: link comum para a página de teste do par (decisão do dono 15), mesmo desenho do botão. Versões
  // "Assine" da Home do site: o "Assine agora" leva ao "Planos e preços", sem cadastro. Fora delas, o botão do cadastro.
  const feixe = motionAtivo ? <span ref={feixeRef} className={motionClasses.feixe} aria-hidden='true' /> : null;
  let cta: ReactNode;
  if (intlPage) {
    cta = (
      <a className={styles.cta} href={homeIntlTrialHref(intlPage)} data-m-cta={m('')}>
        {feixe}
        <TrialCtaText>{mark(t('cta'))}</TrialCtaText>
        {CTA_ARROW}
      </a>
    );
  } else if (isAssine) {
    cta = (
      <Link href='/pricing' prefetch={false} className={styles.cta} data-m-cta={m('')}>
        {feixe}
        <TrialCtaText>{mark(t('cta'))}</TrialCtaText>
        {CTA_ARROW}
      </Link>
    );
  } else {
    cta = (
      <button type='button' className={styles.cta} onClick={() => openTrialSignUp('home')} aria-haspopup='dialog' onPointerEnter={prefetchTrialSignUp} onFocus={prefetchTrialSignUp} data-m-cta={m('')}>
        {feixe}
        {mark(t('cta'))}
        {CTA_ARROW}
      </button>
    );
  }

  return (
    <section ref={sectionRef} data-motion-block={m('videos')} id={HOME_NOVA_VIDEOS_ID} aria-labelledby={visualOnly ? undefined : 'vcs-title'} className={`${HOME_NOVA_BLOCK_CLASS} ${HOME_NOVA_SECTION_CLASS} ${styles.root} pb-[72px] pt-24 text-[16px] leading-[normal] max-[1024px]:pb-16 max-[1024px]:pt-[88px] max-[768px]:pb-14 max-[768px]:pt-[72px]`}>
      {motionAtivo ? <CamadasBloco /> : null}
      <div className={`${HOME_NOVA_INNER_CLASS} ${styles.inner} relative z-[1]`}>
        {!visualOnly && (
          <header data-m={m('texto')} className={`${styles.head} mx-auto mb-12 flex max-w-[720px] flex-col items-center gap-4 text-center max-[768px]:mb-9`}>
            <p className='inline-flex items-center gap-2 rounded-full border border-[rgba(147,255,24,.28)] bg-[rgba(147,255,24,.06)] py-[7px] pe-[14px] ps-[11px] text-[12px] font-semibold uppercase tracking-[.2em] text-hn-primary'>
              <span className='size-1.5 rounded-full bg-hn-primary shadow-[0_0_12px_rgba(147,255,24,.8)]' aria-hidden='true' />
              {mark(t('eyebrow'))}
            </p>
            <h2 id='vcs-title' data-m={m('titulo')} className={`${styles.title} text-balance text-[clamp(32px,3.3vw,48px)] font-bold leading-[1.06] tracking-[-.025em] text-white`}>
              <Trans i18nKey='title' t={t} components={{ green: <em className='not-italic text-hn-primary [text-shadow:0_0_36px_rgba(147,255,24,.32)]' /> }} />
            </h2>
            <p className={`${styles.lead} max-w-[620px] text-pretty text-[16px] leading-[1.55] text-hn-neutral-500`}>{t('lead')}</p>
          </header>
        )}
        {motionAtivo && !visualOnly ? <RotaVideos /> : null}

        {/* A grade é sempre LTR ("Sem" à esquerda, "Com" à direita): os hologramas cobrem o radar do CS2, que fica no
            canto de cima à esquerda do quadro em qualquer idioma. O texto dos hologramas segue o idioma (dir no vidro). */}
        <div ref={gridRef} dir='ltr' data-vcs='' className={`${styles.grid} group grid w-full grid-cols-2 items-stretch gap-6 max-[768px]:grid-cols-1 max-[768px]:gap-4`}>
          {VIDEOS.map((video) => (
            <figure key={video.variant} className={`${styles.card} relative isolate m-0 min-w-0 overflow-hidden rounded-[18px] border bg-[#050605] transition-[transform,box-shadow,border-color] duration-[350ms] ease-[cubic-bezier(.2,.8,.2,1)] after:pointer-events-none after:absolute after:inset-0 after:z-[2] after:rounded-[inherit] after:shadow-[inset_0_0_0_1px_rgba(255,255,255,.06),inset_0_1px_0_rgba(255,255,255,.1)] after:content-[''] hover:translate-y-[-3px] group-hover:translate-y-[-3px] motion-reduce:transition-none ${video.cardClass}`}>
              <Hologram variant={video.variant} mode={t(`holo.${video.key}.mode`)} state={t(`holo.${video.key}.state`)} summary={t(`holo.${video.key}.summary`)} unit={t('holo.unit')} lossLabel={t('holo.loss')} lossValue={formatLoss(HOLOGRAM_FIXED[video.variant].loss)} dir={dir} hideReadouts={hideReadouts} />
              <video className='block aspect-video h-auto w-full bg-black object-cover' muted playsInline loop preload='none' width={1920} height={1080} poster={VIDEO_POSTER_PLACEHOLDER} data-poster-640={`${ASSETS}/${video.file}-poster-640.webp`} data-poster-960={`${ASSETS}/${video.file}-poster-960.webp`} data-poster-1280={`${ASSETS}/${video.file}-poster.webp`} aria-label={t(`video.${video.key}`)} aria-describedby={hideReadouts ? undefined : hologramSummaryId(video.variant)}>
                <source src={`${ASSETS}/${video.file}-720p.mp4`} type='video/mp4' media='(max-width: 768px)' />
                <source src={`${ASSETS}/${video.file}.mp4`} type='video/mp4' />
              </video>
            </figure>
          ))}
        </div>

        {illustrationNote && (
          <p className={visualStyles.illustration} data-main-visual-note='videos'>
            {illustrationNote}
          </p>
        )}
        {!visualOnly && (
          <ul className={`${styles.feats} mx-auto mt-10 grid list-none grid-cols-3 gap-4 p-0 max-[1024px]:grid-cols-1 max-[768px]:mt-8 max-[768px]:gap-3`}>
            {FEATURES.map((feature) => (
              <li key={feature.key} data-m-feat={m('')} className={motionAtivo ? `${styles.feat} relative overflow-hidden ${FEAT_CLASS}` : `${styles.feat} ${FEAT_CLASS}`}>
                {motionAtivo ? <span className={styles.featSweep} data-m-sweep='' aria-hidden='true' /> : null}
                <Image src={`${ASSETS}/${feature.icon}`} alt='' width={20} height={20} className='mt-px size-5 flex-none' />
                <p className={`${styles.featText} m-0 text-[14.5px] leading-normal text-hn-neutral-500`}>
                  <Trans i18nKey={`features.${feature.key}`} t={t} components={{ b: <b className='font-semibold text-white' /> }} />
                </p>
              </li>
            ))}
          </ul>
        )}

        {!visualOnly && cta}
      </div>
    </section>
  );
}
